import { useColorScheme } from "nativewind";
import React, { createContext, useContext, useMemo, useState } from "react";
import { View } from "react-native";
import { derivePalette, PrimaryPalette } from "../utils/derive-palette";
import { buildThemeVars, makeThemeStyle } from "./color-theme";

type ColorScheme = "light" | "dark" | "system";

type ThemeProviderProps = Readonly<{
  children: React.ReactNode;
  initialPrimaryColor?: string;
}>;

type ThemeContextValue = Readonly<{
  theme: ColorScheme;
  resolvedScheme: "light" | "dark";
  primaryPalette: PrimaryPalette;
  /** Plain hex map — read values with themeVars["--color-x"]. */
  themeVars: Record<string, string>;
  /**
   * nativewind `vars()` style object — apply with `style={varStyle}` to
   * register the CSS variables on a subtree (needed inside RN Modals, which
   * render outside the provider's view tree).
   */
  varStyle: any;
  setTheme: (mode: ColorScheme) => void;
  setPrimaryPalette: (palette: PrimaryPalette) => void;
  savedThemeId: string | null;
  setSavedThemeId: (id: string | null) => void;
}>;

const DEFAULT_PRIMARY = "#0669B3";
const DEFAULT_PALETTE = derivePalette(DEFAULT_PRIMARY);

const DEFAULT_CONTEXT: ThemeContextValue = {
  theme: "light",
  resolvedScheme: "light",
  primaryPalette: DEFAULT_PALETTE,
  themeVars: buildThemeVars("light", DEFAULT_PALETTE),
  varStyle: makeThemeStyle("light", DEFAULT_PALETTE),
  setTheme: () => {},
  setPrimaryPalette: () => {},
  savedThemeId: "",
  setSavedThemeId: () => { },
};

export const ThemeContext = createContext<ThemeContextValue>(DEFAULT_CONTEXT);
export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({
  children,
  initialPrimaryColor = DEFAULT_PRIMARY,
}: ThemeProviderProps) => {
  const { colorScheme, setColorScheme } = useColorScheme();
  const [savedThemeId, setSavedThemeId] = useState<string | null>(null);
  const [colorMode, setColorMode] = useState<ColorScheme>("system");
  const [primaryPalette, setPrimaryPalette] = useState<PrimaryPalette>(
    derivePalette(initialPrimaryColor),
  );

  const setTheme = (mode: ColorScheme) => {
    setColorMode(mode);
    setColorScheme(mode === "system" ? "system" : mode);
  };

  const resolvedScheme = (colorScheme ?? "light") as "light" | "dark";

  // Plain map for value reads (themeVars["--color-x"]).
  const themeVars = useMemo(
    () => buildThemeVars(resolvedScheme, primaryPalette),
    [resolvedScheme, primaryPalette],
  );

  // Real vars() style for registering the variables (works on native + web).
  const varStyle = useMemo(
    () => makeThemeStyle(resolvedScheme, primaryPalette),
    [resolvedScheme, primaryPalette],
  );

  return (
    <ThemeContext.Provider
      value={{
        theme: colorMode,
        resolvedScheme,
        primaryPalette,
        themeVars,
        varStyle,
        setTheme,
        setPrimaryPalette,
        savedThemeId,
        setSavedThemeId,
      }}
    >
      <View style={varStyle} className="flex-1">
        {children}
      </View>
    </ThemeContext.Provider>
  );
};
